// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, DollarSign, AlertTriangle, CheckCircle, Users, FileText, Briefcase, ShieldCheck } from 'lucide-react';
import { api } from '../utils/api.js';

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 12,
      padding: '20px 24px',
      flex: '1 1 200px',
      minWidth: 180,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon size={16} style={{ color: 'var(--color-text-3)' }} />
        <span style={{ fontSize: 12, color: 'var(--color-text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)', marginBottom: 16 }}>{title}</h2>
      {children}
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-2)', background: 'var(--color-surface-2)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '10px 14px', color: 'var(--color-text)' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getAdminExtractionHealth()
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--color-text-3)' }}>
      Loading admin metrics...
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--color-red-text)' }}>
      {error}
    </div>
  );

  const { summary, yield_by_catalog_type, green_rate, avg_confidence, failures, cost, top_intervention_catalogs } = data;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', padding: 4 }}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>Extraction Health Dashboard</h1>
      </div>

      {/* Nav to proposals */}
      <button
        onClick={() => navigate('/admin/proposals')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 18px',
          borderRadius: 10,
          border: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
          color: 'var(--color-text)',
          fontWeight: 600,
          fontSize: 13,
          cursor: 'pointer',
          marginBottom: 24,
        }}
      >
        <ShieldCheck size={15} />
        Improvement Proposals
      </button>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        <StatCard icon={Users} label="Users" value={summary.total_users} />
        <StatCard icon={FileText} label="Catalogs" value={summary.total_catalogs} />
        <StatCard icon={Briefcase} label="Jobs" value={summary.total_jobs} sub={`${summary.failed_jobs} failed`} />
        <StatCard icon={DollarSign} label="Total Cost" value={`$${summary.total_cost_usd.toFixed(2)}`} sub={`$${cost.avg_per_catalog.toFixed(4)} avg/catalog`} />
        <StatCard icon={CheckCircle} label="Green Rate" value={`${(green_rate.rate * 100).toFixed(1)}%`} sub={`${green_rate.counts.green}G / ${green_rate.counts.yellow}Y / ${green_rate.counts.red}R`} />
        <StatCard icon={Activity} label="Avg Confidence" value={avg_confidence.toFixed(4)} />
      </div>

      {/* Yield by catalog type */}
      <Section title="Product Yield by Catalog Type">
        <Table
          headers={['Type', 'Catalogs', 'Total Pages', 'Total Products', 'Products/Page']}
          rows={Object.entries(yield_by_catalog_type).map(([type, d]) => [
            type.toUpperCase(),
            d.catalogs,
            d.totalPages,
            d.totalProducts,
            d.totalPages > 0 ? (d.totalProducts / d.totalPages).toFixed(2) : '—',
          ])}
        />
      </Section>

      {/* Failures */}
      <Section title={`Extraction Failures (${failures.total} total)`}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-2)', marginBottom: 8 }}>By Stage</h3>
            <Table
              headers={['Stage', 'Count']}
              rows={Object.entries(failures.by_stage).sort(([,a],[,b]) => b - a).map(([stage, count]) => [stage, count])}
            />
          </div>
          <div style={{ flex: 1, minWidth: 250 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-2)', marginBottom: 8 }}>By Type</h3>
            <Table
              headers={['Type', 'Count']}
              rows={Object.entries(failures.by_type).sort(([,a],[,b]) => b - a).map(([type, count]) => [type, count])}
            />
          </div>
        </div>
      </Section>

      {/* Cost trend */}
      {cost.trend.length > 0 && (
        <Section title="Cost per Catalog (Daily Avg)">
          <Table
            headers={['Date', 'Avg Cost']}
            rows={cost.trend.map(t => [t.date, `$${t.cost.toFixed(4)}`])}
          />
        </Section>
      )}

      {/* Top intervention catalogs */}
      {top_intervention_catalogs.length > 0 && (
        <Section title="Highest Merchant Intervention">
          <Table
            headers={['Catalog ID', 'Resolved Flags']}
            rows={top_intervention_catalogs.map(c => [
              c.catalog_id.slice(0, 8) + '...',
              c.resolved_flags,
            ])}
          />
        </Section>
      )}
    </div>
  );
}
